import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, BookOpen, Video, Image, FileText, ExternalLink, HelpCircle, Calendar, Plus, Trash2, CheckCircle, Upload, X } from "lucide-react";
// Removed Firebase imports - now using server-side uploads
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { QuizzesAPI } from "@/lib/api";

const questionSchema = z.object({
  id: z.string(),
  question: z.string().min(1, "Question is required"),
  options: z.array(z.string().min(1, "Option cannot be empty")).min(2, "At least 2 options required"),
  correctAnswer: z.array(z.number().min(0)).min(1, "At least one correct answer must be selected"),
  type: z.string().default("multiple_choice"),
});

const addTrainingModuleSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  duration: z.coerce.number({ invalid_type_error: "Duration is required" }).int().min(1, "Duration must be at least 1 minute"),
  sequence: z.coerce.number({ invalid_type_error: "Sequence is required" }).int().min(1, "Sequence must be at least 1"),
  day: z.coerce.number({ invalid_type_error: "Day is required" }).int().min(1, "Day must be at least 1"),
  mtype: z.enum(["video", "image", "form", "external"], {
    required_error: "Please select a module type",
  }),
  mediaFile: z.string().optional(),
  externalUrl: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  sizeInBytes: z.number().optional(),
  docServiceUUID: z.string().optional(),
  fileName: z.string().optional(),
  // Quiz configuration
  hasQuiz: z.boolean().default(false),
  quizTitle: z.string().optional(),
  quizDescription: z.string().optional(),
  quizQuestions: z.array(questionSchema).optional(),
  quizPassingScore: z.number().min(0).max(100).optional(),
}).refine((data) => {
  // If quiz is enabled, validate quiz fields
  if (data.hasQuiz) {
    if (!data.quizTitle || data.quizTitle.trim().length === 0) {
      return false;
    }
    if (!data.quizPassingScore || data.quizPassingScore < 0 || data.quizPassingScore > 100) {
      return false;
    }
  }
  return true;
}, {
  message: "Quiz title is required when quiz is enabled and passing score must be between 0-100",
  path: ["quizTitle"]
});

type AddTrainingModuleForm = z.infer<typeof addTrainingModuleSchema>;

export default function AddTrainingModule() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  // Get edit ID from URL query parameters with better detection
  const [location] = useLocation();
  
  // Multiple ways to detect edit mode for robustness
  const urlParams = new URLSearchParams(location.split('?')[1] || '');
  const editModuleIdFromUrl = urlParams.get('edit');
  
  // Also check window location as fallback
  const windowParams = new URLSearchParams(window.location.search);
  const editModuleIdFromWindow = windowParams.get('edit');
  
  const editModuleId = editModuleIdFromUrl || editModuleIdFromWindow;
  
  // Detect edit mode from URL parameters
  const isEditMode = !!editModuleId;
  
  // URL detection successfully configured
  const [questions, setQuestions] = useState<Array<{
    id: string;
    question: string;
    options: string[];
    correctAnswer: number[];
    type: string;
  }>>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Fetch existing training modules to calculate next sequence
  const { data: existingModules } = useQuery({
    queryKey: ['/training-modules'],
  }) as { data: { modules: any[] } | undefined };
  
  // Fetch specific module data when in edit mode
  const { data: editModuleData, isLoading: isLoadingEditData } = useQuery({
    queryKey: [`/training-modules/${editModuleId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/training-modules/${editModuleId}`);
      return response.json();
    },
    enabled: !!editModuleId,
  }) as { data: { module: any } | undefined, isLoading: boolean };

  const form = useForm<AddTrainingModuleForm>({
    resolver: zodResolver(addTrainingModuleSchema),
    defaultValues: {
      title: "",
      description: "",
      duration: 15,
      sequence: 1,
      day: 1,
      mtype: "video",
      mediaFile: "",
      externalUrl: "",
      sizeInBytes: undefined,
      docServiceUUID: "",
      fileName: "",
      hasQuiz: false,
      quizTitle: "",
      quizDescription: "",
      quizQuestions: [],
      quizPassingScore: 70,
    },
  });

  // Function to calculate next sequence number for a given day
  const getNextSequenceForDay = (day: number) => {
    if (!existingModules?.modules) return 1;
    
    const dayModules = existingModules.modules.filter((module: any) => module.day === day);
    if (dayModules.length === 0) return 1;
    
    const maxSequence = Math.max(...dayModules.map((module: any) => module.sequence));
    return maxSequence + 1;
  };

  // Function to check if sequence already exists for the given day (excluding current module in edit mode)
  const isSequenceTaken = (day: number, sequence: number) => {
    if (!existingModules?.modules) return false;
    
    const dayModules = existingModules.modules.filter((module: any) => module.day === day);
    return dayModules.some((module: any) => 
      module.sequence === sequence && 
      (!isEditMode || module.id.toString() !== editModuleId) // Exclude current module in edit mode
    );
  };

  // Fetch quiz data for the module being edited
  const { data: quizzesData } = useQuery({
    queryKey: ['/quizzes'],
    enabled: isEditMode,
  }) as { data: { quizzes: any[] } | undefined };
  
  // Populate form when editing existing module
  useEffect(() => {
    if (isEditMode && editModuleData?.module) {
      const module = editModuleData.module;
      
      // Find associated quiz if exists
      const associatedQuiz = quizzesData?.quizzes?.find(
        (quiz: any) => quiz.moduleId === parseInt(editModuleId!)
      );
      
      // Reset form with module data
      form.reset({
        title: module.title || "",
        description: module.description || "",
        duration: module.duration || 15,
        sequence: module.sequence || 1,
        day: module.day || 1,
        mtype: module.mtype || "video",
        mediaFile: module.mediaFile || "",
        externalUrl: module.externalUrl || "",
        sizeInBytes: module.sizeInBytes,
        docServiceUUID: module.docServiceUUID || "",
        fileName: module.fileName || "",
        hasQuiz: !!associatedQuiz,
        quizTitle: associatedQuiz?.title || "",
        quizDescription: associatedQuiz?.description || "",
        quizQuestions: associatedQuiz?.questions?.map((q: any) => ({
          ...q,
          correctAnswer: Array.isArray(q.correctAnswer) ? q.correctAnswer : (q.correctAnswer !== undefined ? [q.correctAnswer] : [])
        })) || [],
        quizPassingScore: associatedQuiz?.passingScore || 70,
      });
      
      // Set quiz questions state if exists
      if (associatedQuiz?.questions) {
        setQuestions(associatedQuiz.questions.map((q: any) => ({
          id: q.id || `q${Date.now()}`,
          question: q.question || "",
          options: q.options || ["", ""],
          correctAnswer: Array.isArray(q.correctAnswer) ? q.correctAnswer : (q.correctAnswer !== undefined ? [q.correctAnswer] : []),
          type: q.type || "multiple_choice"
        })));
      }
    }
  }, [isEditMode, editModuleData, quizzesData, editModuleId, form]);
  
  // Set initial sequence when creating a new module
  useEffect(() => {
    if (!isEditMode && existingModules?.modules) {
      const initialDay = form.getValues("day");
      const nextSequence = getNextSequenceForDay(initialDay);
      form.setValue("sequence", nextSequence);
    }
  }, [isEditMode, existingModules, form, getNextSequenceForDay]);

  // Auto-calculate sequence when day changes (only in create mode)
  useEffect(() => {
    if (!isEditMode) {
      const subscription = form.watch((value, { name }) => {
        if (name === "day" && value.day) {
          const nextSequence = getNextSequenceForDay(value.day);
          form.setValue("sequence", nextSequence);
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [isEditMode, existingModules, form, getNextSequenceForDay]);


  // Quiz management functions
  const addQuestion = () => {
    const newQuestion = {
      id: `q${Date.now()}`,
      question: "",
      options: ["", ""],
      correctAnswer: [],
      type: "multiple_choice",
    };
    setQuestions([...questions, newQuestion]);
  };

  const removeQuestion = (questionId: string) => {
    setQuestions(questions.filter(q => q.id !== questionId));
  };

  const updateQuestion = (questionId: string, field: string, value: any) => {
    setQuestions(questions.map(q => 
      q.id === questionId ? { ...q, [field]: value } : q
    ));
  };

  const addOption = (questionId: string) => {
    setQuestions(questions.map(q => 
      q.id === questionId 
        ? { ...q, options: [...q.options, ""] }
        : q
    ));
  };

  const removeOption = (questionId: string, optionIndex: number) => {
    setQuestions(questions.map(q => 
      q.id === questionId 
        ? { ...q, options: q.options.filter((_, index) => index !== optionIndex) }
        : q
    ));
  };

  const updateOption = (questionId: string, optionIndex: number, value: string) => {
    setQuestions(questions.map(q => 
      q.id === questionId 
        ? { ...q, options: q.options.map((opt, index) => index === optionIndex ? value : opt) }
        : q
    ));
  };

  const toggleCorrectAnswer = (questionId: string, optionIndex: number) => {
    setQuestions(questions.map(q => 
      q.id === questionId 
        ? { 
            ...q, 
            correctAnswer: q.correctAnswer.includes(optionIndex)
              ? q.correctAnswer.filter(index => index !== optionIndex)
              : [...q.correctAnswer, optionIndex]
          }
        : q
    ));
  };

  const isCorrectAnswer = (questionId: string, optionIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    return question ? question.correctAnswer.includes(optionIndex) : false;
  };

  // File upload functions
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // No file type or size restrictions - accept any file
      console.log(`File selected: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB, type: ${file.type}`);

      setSelectedFile(file);
      form.setValue("fileName", file.name);
      form.setValue("sizeInBytes", file.size);
    }
  };

  const uploadFileToFirebase = async (file: File): Promise<string> => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      console.log("Starting direct Firebase upload for file:", file.name);
      
      // Import Firebase functions dynamically
      const { storage, ensureAuthenticated } = await import('@/lib/firebase');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      
      // Ensure user is authenticated with Google OAuth
      await ensureAuthenticated();
      
      // Create a storage reference
      const timestamp = Date.now();
      const fileName = `training-modules/${timestamp}-${file.name}`;
      const storageRef = ref(storage, fileName);
      
      console.log('Uploading to Firebase Storage:', fileName);
      setUploadProgress(25);
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type
      });
      
      console.log('Upload successful, getting download URL...');
      setUploadProgress(75);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('Download URL obtained:', downloadURL);
      setUploadProgress(100);
      
      return downloadURL;
    } catch (error) {
      console.error("Firebase upload error:", error);
      
      let errorMessage = "Failed to upload file. Please try again.";
      let errorTitle = "Upload Error";
      
      if (error instanceof Error) {
        // Check for Firebase configuration errors
        if (error.message.includes('Firebase project configuration not found') ||
            error.message.includes('configuration-not-found') ||
            error.message.includes('project ID doesn\'t exist')) {
          errorTitle = "Firebase Configuration Error";
          errorMessage = "Firebase project is not properly configured. Please contact your administrator. " +
            "The Firebase project 'sfy-launchpad' may not exist or the service account may not have access.";
        } else if (error.message.includes('auth') || error.message.includes('authentication')) {
          errorMessage = "Please sign in with Google to upload files.";
        } else if (error.message.includes('permission') || error.message.includes('unauthorized')) {
          errorTitle = "Permission Denied";
          errorMessage = "You don't have permission to upload files. Please contact your administrator.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
      });
      
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    form.setValue("fileName", "");
    form.setValue("sizeInBytes", undefined);
    form.setValue("externalUrl", "");
  };

  const onSubmit = async (data: AddTrainingModuleForm) => {
    setIsSubmitting(true);
    try {
      // Auto-calculate sequence for create mode (already done in useEffect, but ensure it's set)
      if (!isEditMode) {
        data.sequence = getNextSequenceForDay(data.day);
      }

      // Upload file directly to Firebase if a file is selected
      if (selectedFile && (data.mtype === 'video' || data.mtype === 'image')) {
        try {
          const downloadURL = await uploadFileToFirebase(selectedFile);
          data.externalUrl = downloadURL; // Store Firebase URL in externalUrl field
          data.mediaFile = downloadURL;   // Also store in mediaFile for compatibility
        } catch (error) {
          // Error toast is already shown in uploadFileToFirebase
          return;
        }
      }

      // Validate quiz data if quiz is enabled
      if (data.hasQuiz) {
        if (questions.length === 0) {
          toast({
            title: "Validation Error",
            description: "Please add at least one question to the quiz",
            variant: "destructive",
          });
          return;
        }
        
        // Validate each question
        for (const question of questions) {
          if (!question.question.trim()) {
            toast({
              title: "Validation Error",
              description: "All questions must have text",
              variant: "destructive",
            });
            return;
          }
          
          if (question.options.length < 2) {
            toast({
              title: "Validation Error",
              description: "Each question must have at least 2 options",
              variant: "destructive",
            });
            return;
          }
          
          if (question.options.some(option => !option.trim())) {
            toast({
              title: "Validation Error",
              description: "All question options must be filled",
              variant: "destructive",
            });
            return;
          }
          
          if (question.correctAnswer.length === 0) {
            toast({
              title: "Validation Error",
              description: "Please select at least one correct answer for each question",
              variant: "destructive",
            });
            return;
          }
        }
      }

      // Create or update the training module
      const method = isEditMode ? "PUT" : "POST";
      const url = isEditMode ? `/training-modules/${editModuleId}` : "/training-modules";
      const moduleResponse = await apiRequest(method, url, data);
      
      // Check if the request was successful
      if (!moduleResponse.ok) {
        const errorData = await moduleResponse.json();
        throw new Error(errorData.message || (isEditMode ? "Failed to update training module" : "Failed to create training module"));
      }
      
      const moduleData = await moduleResponse.json();
      
      // Handle quiz creation or update
      if (data.hasQuiz && questions.length > 0) {
        const quizData = {
          moduleId: moduleData.module.id.toString(), // Ensure it's a string as expected by API
          title: data.quizTitle || `${data.title} Quiz`,
          description: data.quizDescription || `Quiz for ${data.title}`,
          questions: questions,
          passingScore: data.quizPassingScore || 70,
        };
        
        // Send questions with correctAnswer as array (supports both single and multiple answers)
        const transformedQuizData = {
          ...quizData,
          questions: questions.map(q => ({
            ...q,
            // Ensure correctAnswer is always an array
            correctAnswer: Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer]
          }))
        };
        
        // Check if we're editing and there's an existing quiz
        const existingQuiz = isEditMode && quizzesData?.quizzes?.find(
          (quiz: any) => quiz.moduleId === parseInt(editModuleId!)
        );
        
        if (existingQuiz) {
          // Update existing quiz
          await QuizzesAPI.update(existingQuiz.id, transformedQuizData);
        } else {
          // Create new quiz
          await QuizzesAPI.create(transformedQuizData);
        }
      }
      
      toast({
        title: "Success! 🎉",
        description: isEditMode ? "Training module updated successfully!" : "Training module created successfully!",
        variant: "default",
      });
      queryClient.invalidateQueries({ queryKey: ['/training-modules'] });
      queryClient.invalidateQueries({ queryKey: ['/quizzes'] });
      setLocation('/training');
    } catch (error) {
      console.error("Error creating training module:", error);
      
      let errorMessage = "Failed to create module";
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModuleTypeIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="h-4 w-4" />;
      case "image":
        return <Image className="h-4 w-4" />;
      case "form":
        return <FileText className="h-4 w-4" />;
      case "helpdesk":
        return <HelpCircle className="h-4 w-4" />;
      case "external":
        return <ExternalLink className="h-4 w-4" />;
      default:
        return <BookOpen className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-servify-white-grey py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation('/training')}
            className="text-gray-600 hover:text-servify-dark-grey"
            data-testid="button-back-to-training"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Knowledge Hub
          </Button>
        </div>

        {/* Form Card */}
        <Card className="shadow-xl shadow-servify-purple/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-servify-dark-grey">
              {isEditMode ? "Edit Knowledge Hub Module" : "Add New Knowledge Hub Module"}
            </CardTitle>
            <p className="text-gray-600 text-sm">
              {isEditMode ? "Update the knowledge hub module for the induction program." : "Create a new knowledge hub module for the induction program."}
            </p>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-servify-dark-grey border-b border-servify-light-grey pb-2">Basic Information</h3>
                  
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-servify-dark-grey">Module Title</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            placeholder="Enter module title"
                            data-testid="input-module-title"
                            className="h-10 border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple" 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-servify-dark-grey">Description</FormLabel>
                        <FormControl>
                          <Textarea 
                            {...field} 
                            placeholder="Enter module description"
                            data-testid="textarea-module-description"
                            className="border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple"
                            rows={3}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                </div>

                {/* Module Configuration */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-servify-dark-grey border-b border-servify-light-grey pb-2">Module Configuration</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="duration"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-servify-dark-grey">Duration (minutes)</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              {...field}
                              onChange={e => {
                                const value = e.target.value === "" ? undefined : Number(e.target.value);
                                field.onChange(value);
                              }}
                              value={field.value ?? ''}
                              data-testid="input-module-duration"
                              className="h-10 border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="day"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-servify-dark-grey">Day</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              {...field}
                              onChange={e => {
                                const value = e.target.value === "" ? undefined : Number(e.target.value);
                                field.onChange(value);
                              }}
                              value={field.value ?? ''}
                              data-testid="input-module-day"
                              className="h-10 border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Auto-calculated Sequence Display */}
                  {!isEditMode && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-blue-600" />
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Sequence:</span> This module will be assigned sequence <span className="font-bold">#{form.watch("sequence")}</span> for Day {form.watch("day")}
                        </p>
                      </div>
                    </div>
                  )}

                  <FormField
                    control={form.control}
                    name="mtype"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700 mb-2 block">Module Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger 
                              data-testid="select-module-type"
                              className="w-full h-11 border-2 border-slate-200 rounded-lg bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200"
                            >
                              <SelectValue placeholder="Select module type" className="text-slate-500" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="z-[99999] bg-white border-2 border-purple-200 rounded-lg shadow-xl">
                            <SelectItem value="video" data-testid="option-type-video" className="hover:bg-purple-50 cursor-pointer text-slate-700">
                              <div className="flex items-center gap-2">
                                <Video className="h-4 w-4 text-servify-purple" />
                                Video
                              </div>
                            </SelectItem>
                            <SelectItem value="image" data-testid="option-type-image" className="hover:bg-purple-50 cursor-pointer text-slate-700">
                              <div className="flex items-center gap-2">
                                <Image className="h-4 w-4 text-servify-purple" />
                                Image
                              </div>
                            </SelectItem>
                            <SelectItem value="external" data-testid="option-type-external" className="hover:bg-purple-50 cursor-pointer text-slate-700">
                              <div className="flex items-center gap-2">
                                <ExternalLink className="h-4 w-4 text-servify-purple" />
                                External Link
                              </div>
                            </SelectItem>

                            <SelectItem value="form" data-testid="option-type-form" className="hover:bg-purple-50 cursor-pointer text-slate-700">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-servify-purple" />
                                Form
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* File Upload for Video and Image */}
                  {form.watch("mtype") === "video" || form.watch("mtype") === "image" ? (
                    <div className="space-y-4">
                      <FormLabel className="text-sm font-medium text-servify-dark-grey flex items-center gap-2">
                        <Upload className="h-4 w-4 text-gray-500" />
                        Upload {form.watch("mtype") === "video" ? "Video" : "Image"} File
                      </FormLabel>
                      
                      {/* Show existing file if in edit mode and no new file selected */}
                      {!selectedFile && isEditMode && (form.watch("mediaFile") || form.watch("externalUrl")) ? (
                        <div className="border border-servify-light-grey rounded-lg p-4 bg-blue-50">
                          <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                              {form.watch("mtype") === "video" ? (
                                <Video className="h-8 w-8 text-servify-purple" />
                              ) : (
                                <Image className="h-8 w-8 text-servify-purple" />
                              )}
                              <div className="flex-1">
                                <p className="text-sm font-medium text-servify-dark-grey">
                                  {form.watch("fileName") || "Existing File"}
                                </p>
                                {form.watch("sizeInBytes") && (
                                  <p className="text-xs text-gray-500">
                                    {(form.watch("sizeInBytes")! / (1024 * 1024)).toFixed(2)} MB
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Show video player for existing video */}
                            {form.watch("mtype") === "video" && (form.watch("mediaFile") || form.watch("externalUrl")) && (
                              <div className="mt-2">
                                <video
                                  controls
                                  className="w-full rounded-lg max-h-64"
                                  src={form.watch("mediaFile") || form.watch("externalUrl")}
                                  data-testid="existing-video-preview"
                                >
                                  Your browser does not support the video tag.
                                </video>
                              </div>
                            )}
                            
                            {/* Show image preview for existing image */}
                            {form.watch("mtype") === "image" && (form.watch("mediaFile") || form.watch("externalUrl")) && (
                              <div className="mt-2">
                                <img
                                  src={form.watch("mediaFile") || form.watch("externalUrl")}
                                  alt="Existing file preview"
                                  className="w-full rounded-lg max-h-64 object-contain"
                                  data-testid="existing-image-preview"
                                />
                              </div>
                            )}
                            
                            {/* Replace file option */}
                            <div className="relative border-2 border-dashed border-servify-purple/30 rounded-lg p-4 text-center hover:border-servify-purple transition-colors cursor-pointer">
                              <p className="text-sm text-servify-purple font-medium">
                                Click to replace with a new file
                              </p>
                              <input
                                type="file"
                                accept={form.watch("mtype") === "video" 
                                  ? "video/mp4,video/webm,video/ogg" 
                                  : "image/jpeg,image/jpg,image/png,image/gif,image/webp"}
                                onChange={handleFileSelect}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                data-testid="file-replace-input"
                              />
                            </div>
                          </div>
                        </div>
                      ) : !selectedFile ? (
                        <div className="relative border-2 border-dashed border-servify-light-grey rounded-lg p-6 text-center hover:border-servify-purple transition-colors cursor-pointer">
                          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">
                              Any file type and size accepted
                            </p>
                          </div>
                          <input
                            type="file"
                            accept={form.watch("mtype") === "video" 
                              ? "video/mp4,video/webm,video/ogg" 
                              : "image/jpeg,image/jpg,image/png,image/gif,image/webp"}
                            onChange={handleFileSelect}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            data-testid="file-upload-input"
                          />
                        </div>
                      ) : (
                        <div className="border border-servify-light-grey rounded-lg p-4 bg-servify-white-grey">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {form.watch("mtype") === "video" ? (
                                <Video className="h-8 w-8 text-servify-purple" />
                              ) : (
                                <Image className="h-8 w-8 text-servify-purple" />
                              )}
                              <div>
                                <p className="text-sm font-medium text-servify-dark-grey">{selectedFile.name}</p>
                                <p className="text-xs text-gray-500">
                                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={removeSelectedFile}
                              className="text-servify-medium-grey hover:text-servify-purple"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {isUploading && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                <span>Uploading...</span>
                                <span>{uploadProgress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-servify-purple h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${uploadProgress}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : null}

                  {/* Conditional External URL Input */}
                  {form.watch("mtype") === "external" ? (
                    <FormField
                      control={form.control}
                      name="externalUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-servify-dark-grey flex items-center gap-2">
                            <ExternalLink className="h-4 w-4 text-gray-500" />
                            External URL
                          </FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              placeholder="Enter external URL"
                              data-testid="input-external-url"
                              className="h-10 border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple" 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}

                  {/* Conditional Form Link Input */}
                  {form.watch("mtype") === "form" ? (
                    <FormField
                      control={form.control}
                      name="externalUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-servify-dark-grey flex items-center gap-2">
                            <ExternalLink className="h-4 w-4 text-gray-500" />
                            Form Link
                          </FormLabel>
                          <FormControl>
                            <Input 
                              {...field} 
                              placeholder="Enter form link"
                              data-testid="input-form-link"
                              className="h-10 border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple" 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}
                </div>

                {/* Quiz Configuration */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-servify-dark-grey border-b border-servify-light-grey pb-2">Quiz Configuration (Optional)</h3>
                  
                  <FormField
                    control={form.control}
                    name="hasQuiz"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="checkbox-has-quiz"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-medium text-servify-dark-grey">
                            Include a quiz for this module
                          </FormLabel>
                          <p className="text-xs text-gray-500">
                            Add a knowledge assessment quiz to test understanding of the module content
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />

                  {form.watch("hasQuiz") && (
                    <div className="space-y-6 p-4 bg-servify-white-grey rounded-lg border border-servify-light-grey">
                      {/* Quiz Basic Info */}
                      <div className="space-y-4">
                        <h4 className="text-md font-medium text-gray-800">Quiz Details</h4>
                        
                        <FormField
                          control={form.control}
                          name="quizTitle"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-servify-dark-grey">Quiz Title</FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  placeholder="Enter quiz title"
                                  data-testid="input-quiz-title"
                                  className="h-10 border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple" 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="quizDescription"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-servify-dark-grey">Quiz Description</FormLabel>
                              <FormControl>
                                <Textarea 
                                  {...field} 
                                  placeholder="Enter quiz description"
                                  data-testid="textarea-quiz-description"
                                  className="border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple"
                                  rows={2}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="quizPassingScore"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-servify-dark-grey">Passing Score (%)</FormLabel>
                              <FormControl>
                                <Input 
                                  {...field} 
                                  type="number"
                                  min="0"
                                  max="100"
                                  placeholder="70"
                                  data-testid="input-quiz-passing-score"
                                  className="h-10 border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple" 
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 70)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Quiz Questions */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-md font-medium text-gray-800">Quiz Questions</h4>
                          <Button
                            type="button"
                            onClick={addQuestion}
                            size="sm"
                            className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                            data-testid="button-add-question"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Question
                          </Button>
                        </div>

                        {questions.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>No questions added yet. Click "Add Question" to get started.</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {questions.map((question, questionIndex) => (
                              <Card key={question.id} className="border border-servify-light-grey">
                                <CardHeader className="pb-3">
                                  <div className="flex items-center justify-between">
                                    <h5 className="text-sm font-medium text-servify-dark-grey">Question {questionIndex + 1}</h5>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeQuestion(question.id)}
                                      className="text-servify-purple hover:text-servify-purple-dark hover:bg-servify-purple/10"
                                      data-testid={`button-remove-question-${questionIndex}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                  {/* Question Text */}
                                  <div>
                                    <label className="text-sm font-medium text-servify-dark-grey mb-2 block">
                                      Question Text
                                    </label>
                                    <Input
                                      value={question.question}
                                      onChange={(e) => updateQuestion(question.id, 'question', e.target.value)}
                                      placeholder="Enter your question here"
                                      data-testid={`input-question-${questionIndex}`}
                                      className="h-10 border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple"
                                    />
                                  </div>

                                  {/* Options */}
                                  <div>
                                    <label className="text-sm font-medium text-servify-dark-grey mb-2 block">
                                      Answer Options
                                    </label>
                                    <div className="space-y-2">
                                      {question.options.map((option, optionIndex) => (
                                        <div key={optionIndex} className="flex items-center space-x-2">
                                          <div className="flex items-center space-x-2 flex-1">
                                            <Checkbox
                                              checked={isCorrectAnswer(question.id, optionIndex)}
                                              onCheckedChange={() => toggleCorrectAnswer(question.id, optionIndex)}
                                              className="text-servify-purple focus:ring-servify-purple"
                                              data-testid={`checkbox-correct-${questionIndex}-${optionIndex}`}
                                            />
                                            <Input
                                              value={option}
                                              onChange={(e) => updateOption(question.id, optionIndex, e.target.value)}
                                              placeholder={`Option ${optionIndex + 1}`}
                                              data-testid={`input-option-${questionIndex}-${optionIndex}`}
                                              className="h-8 border-servify-light-grey focus:border-servify-purple focus:ring-1 focus:ring-servify-purple"
                                            />
                                          </div>
                                          {question.options.length > 2 && (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => removeOption(question.id, optionIndex)}
                                              className="text-servify-purple hover:text-servify-purple-dark hover:bg-servify-purple/10"
                                              data-testid={`button-remove-option-${questionIndex}-${optionIndex}`}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          )}
                                        </div>
                                      ))}
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => addOption(question.id)}
                                        className="w-full"
                                        data-testid={`button-add-option-${questionIndex}`}
                                      >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Option
                                      </Button>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-4 pt-6 border-t border-servify-light-grey">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLocation('/training')}
                    data-testid="button-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="button-submit-module"
                    className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                  >
                    {isSubmitting 
                      ? (isEditMode ? "Updating..." : "Creating...") 
                      : (isEditMode ? "Update Module" : "Create Module")}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 